import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/** Where every project starts: the "Where will your model run?" page. */
export const NEW_PROJECT_PATH = '/dashboard/start';

type Props = {
  /** Icon-only, for the collapsed sidebar; the label moves to a tooltip. */
  collapsed?: boolean;
  onNavigate?: () => void;
  className?: string;
};

/** Primary entry to the start page, shared by the desktop sidebar and the
 *  mobile drawer. The logo goes to Projects, so the start page gets its own
 *  explicit action rather than a "Home" item that would compete with it. */
export function NewProjectButton({ collapsed = false, onNavigate, className }: Props) {
  const { t } = useTranslation('common');
  const label = t('nav.newProject');

  const button = (
    <Button
      asChild
      variant="soft"
      size={collapsed ? 'icon' : 'default'}
      className={cn(!collapsed && 'w-full justify-start', className)}
    >
      <Link to={NEW_PROJECT_PATH} onClick={onNavigate} aria-label={collapsed ? label : undefined}>
        <Plus className="h-4 w-4" />
        {!collapsed && label}
      </Link>
    </Button>
  );

  if (!collapsed) return button;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={8}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}
